package com.example.mystybag.appCore.views

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.DeleteAccountRequestViewModel
import com.example.mystybag.appCore.views.components.ClientWebHeaderBar
import com.example.mystybag.appCore.views.components.WebHeaderBottomDivider
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonSize
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.expected.rememberIsWebDesktopLayout
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme
import com.example.mystybag.core.ui.theme.LocalTheme.carosoft
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.common.ui.views.UIView
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.fillMaxSize
import dev.icerock.moko.resources.compose.painterResource
import androidx.compose.ui.text.style.TextAlign

@Composable
fun DeleteAccountRequestView(viewModel: DeleteAccountRequestViewModel) {
    val uiState by viewModel.uiState.subscribeAsState()
    val keyboardController = LocalSoftwareKeyboardController.current
    val colors = AppTheme.current.colors
    // Use the wide desktop layout only on landscape Web; portrait Web (phone) and native
    // fall back to the mobile layout.
    val isWeb = rememberIsWebDesktopLayout()

    UIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        showTopBar = !isWeb,
        navigationContent = if (!isWeb) {
            {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = p16),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .size(p48)
                        .clip(CircleShape)
                        .background(colors.surfaceContainerLowest)
                        .noRippleClickable {
                            keyboardController?.hide()
                            viewModel.navCallbacks.backButtonCallback()
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        painter = painterResource(SRC.images.arrow_back),
                        contentDescription = null,
                        contentScale = ContentScale.Fit,
                        modifier = Modifier.size(width = 10.dp, height = 18.dp),
                        colorFilter = ColorFilter.tint(colors.primary)
                    )
                }

                Texts.TitleMedium(
                    text = sharedString(SR.strings.delete_account),
                    modifier = Modifier.weight(1f),
                    textAlign = TextAlign.Center,
                    color = colors.onSurface
                )

                Spacer(modifier = Modifier.size(p48))
            }
        }
        } else null
    ) {
        if (isWeb) {
            Column(modifier = Modifier.fillMaxSize()) {
                ClientWebHeaderBar(
                    selectedTab = null,
                    homeTabIndex = 0,
                    deliveryTabIndex = 1,
                    favoritesTabIndex = 2,
                    menuTabIndex = 3,
                    searchCallbacks = null,
                    onFilterClick = {},
                    showSearchAndFilter = false,
                    showBackButton = true,
                    onBackClick = viewModel::onBackClick,
                    onHomeClick = null,
                    onOrdersClick = null,
                    onFavoritesClick = null,
                    onMenuClick = null
                )
                WebHeaderBottomDivider()
                Box(
                    modifier = Modifier.fillMaxWidth().fillMaxHeight(),
                    contentAlignment = Alignment.TopCenter
                ) {
                    DeleteAccountRequestContent(
                        uiState = uiState,
                        viewModel = viewModel,
                        isWeb = true,
                        modifier = Modifier
                            .width(1080.dp)
                            .fillMaxHeight()
                    )
                }
            }
        } else {
            DeleteAccountRequestContent(
                uiState = uiState,
                viewModel = viewModel,
                isWeb = false,
                modifier = Modifier.fillMaxSize()
            )
        }
    }
}

@Composable
private fun DeleteAccountRequestContent(
    uiState: com.example.mystybag.appCore.model.pages.DeleteAccountRequestUIState,
    viewModel: DeleteAccountRequestViewModel,
    isWeb: Boolean,
    modifier: Modifier
) {
    val colors = AppTheme.current.colors
    if (uiState.hasPendingRequest) {
        Column(
            modifier = modifier
                .fillMaxWidth()
                .background(colors.background)
                .padding(p16),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Texts.HeadlineSmall(
                text = sharedString(SR.strings.delete_account_request_pending_message),
                color = colors.onSurface,
                textAlign = TextAlign.Center
            )
        }
    } else {
        // verticalScroll подава на съдържанието неограничена височина (maxHeight = Infinity),
        // затова Column с fillMaxSize() не се разтяга. Решение: BoxWithConstraints дава
        // крайна височина; heightIn(min = viewportHeight) прави scroll съдържанието поне толкова високо.
        BoxWithConstraints(modifier = modifier.fillMaxSize()) {
            val viewportHeight = maxHeight
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .heightIn(min = viewportHeight)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .then(
                            if (!isWeb) Modifier.heightIn(min = viewportHeight)
                            else Modifier
                        )
                        .background(colors.background)
                        .padding(start = p16, end = p16, top = p24),
                    verticalArrangement = if (isWeb) Arrangement.Top else Arrangement.spacedBy(p24)
                ) {
                    Column(verticalArrangement = Arrangement.spacedBy(p24)) {
                        Texts.HeadlineSmall(
                            text = sharedString(SR.strings.submit_request),
                            color = colors.onSurface
                        )

                        Texts.BodyMedium(
                            text = sharedString(SR.strings.delete_account_request_description),
                            color = colors.onSurfaceVariant
                        )

                        Texts.BodyMedium(
                            text = sharedString(SR.strings.delete_account_request_tap_to_send),
                            color = colors.onSurfaceVariant
                        )

                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(min = 120.dp)
                                .background(color = colors.surface, shape = RoundedCornerShape(p12))
                                .border(1.dp, colors.outlineVariant, RoundedCornerShape(p12))
                                .padding(p16)
                        ) {
                            BasicTextField(
                                value = uiState.enquiryText,
                                onValueChange = { viewModel.onEnquiryChanged(it) },
                                modifier = Modifier.fillMaxWidth(),
                                maxLines = 6,
                                singleLine = false,
                                textStyle = TextStyle(
                                    fontFamily = carosoft,
                                    fontWeight = FontWeight.Normal,
                                    fontSize = 16.sp,
                                    lineHeight = 24.sp,
                                    color = colors.onSurface
                                ),
                                cursorBrush = SolidColor(colors.primary),
                                decorationBox = { inner ->
                                    Box(modifier = Modifier.fillMaxWidth()) {
                                        if (uiState.enquiryText.isEmpty()) {
                                            Texts.BodyLarge(
                                                text = sharedString(SR.strings.your_enquiry),
                                                color = colors.onSurfaceVariant
                                            )
                                        }
                                        inner()
                                    }
                                }
                            )
                        }
                    }

                    if (isWeb) {
                        Spacer(modifier = Modifier.height(p32))
                    } else {
                        Spacer(modifier = Modifier.weight(1f))
                    }

                    if (isWeb) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.Center
                        ) {
                            DefaultButton(
                                modifier = Modifier
                                    .fillMaxWidth(0.5f)
                                    .height(p56),
                                caption = sharedString(SR.strings.contact_support),
                                size = DefaultButtonSize.LARGE,
                                type = DefaultButtonType.PRIMARY,
                                enabled = !uiState.isLoading && uiState.enquiryText.isNotBlank(),
                                callback = { viewModel.onSubmitClick() },
                                cornerRadius = 100.dp
                            )
                        }
                    } else {
                        DefaultButton(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(p56),
                            caption = sharedString(SR.strings.contact_support),
                            size = DefaultButtonSize.LARGE,
                            type = DefaultButtonType.PRIMARY,
                            enabled = !uiState.isLoading && uiState.enquiryText.isNotBlank(),
                            callback = { viewModel.onSubmitClick() },
                            cornerRadius = 100.dp
                        )
                    }
                }
            }
        }
    }
}
